// ⌘K / Ctrl K: jump to any order, customer or page. Plus the keyboard map and "?" sheet.
// Actions are limited to Zen verbs — there is no "advance stage".
import { SearchOutlined } from '@ant-design/icons';
import { Input, Modal, type InputRef } from 'antd';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasBillingAccess, hasCapability } from './access';
import { SHORTCUTS } from './shortcuts';
import { useZenBootstrap } from './hooks';
import { orderStatus } from './status';
import { OrderNumber, StatusPill } from './ui';

interface PaletteEntry {
  id: string;
  group: 'Orders' | 'Customers' | 'Go to' | 'Actions';
  label: ReactNode;
  keywords: string;
  hint?: ReactNode;
  run: () => void;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data } = useZenBootstrap();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<InputRef>(null);

  const entries = useMemo<PaletteEntry[]>(() => {
    if (!data) return [];
    const user = data.currentUser;
    const go = (path: string) => () => navigate(path);
    const pages: PaletteEntry[] = [
      ...(user.isAdmin || hasBillingAccess(user) ? [{ id: 'p-dash', group: 'Go to' as const, label: 'Dashboard', keywords: 'dashboard today', hint: 'G D', run: go('/dashboard') }] : []),
      ...(hasCapability(user, data.departments, 'queue') ? [{ id: 'p-queue', group: 'Go to' as const, label: 'My queue', keywords: 'queue my jobs', hint: 'G Q', run: go('/queue') }] : []),
      ...(hasCapability(user, data.departments, 'orders') ? [{ id: 'p-orders', group: 'Go to' as const, label: 'Work orders', keywords: 'orders work', hint: 'G O', run: go('/work-orders') }] : []),
      ...(hasBillingAccess(user) ? [{ id: 'p-accounts', group: 'Go to' as const, label: 'Accounts & ledger', keywords: 'accounts ledger invoice', run: go('/accounts') }] : []),
      ...(user.isAdmin ? [
        { id: 'p-admin', group: 'Go to' as const, label: 'Administration', keywords: 'admin users departments', run: go('/admin') },
        { id: 'p-audit', group: 'Go to' as const, label: 'Audit log', keywords: 'audit history log', run: go('/audit') },
      ] : []),
    ];
    const actions: PaletteEntry[] = hasCapability(user, data.departments, 'create-order')
      ? [{ id: 'a-new', group: 'Actions', label: '+ New work order', keywords: 'new create order', hint: 'N', run: go('/work-orders/new') }]
      : [];
    const orders: PaletteEntry[] = hasCapability(user, data.departments, 'orders')
      ? data.workOrders.filter((order) => !order.deletedAt).map((order) => ({
        id: `o-${order.id}`,
        group: 'Orders',
        label: <><OrderNumber>{order.orderNumber}</OrderNumber> · {order.customerName}</>,
        keywords: `${order.orderNumber} ${order.customerName} ${order.customerPhone}`.toLowerCase(),
        hint: <StatusPill status={orderStatus(order)} />,
        run: go(`/work-orders/${order.id}`),
      }))
      : [];
    const customers: PaletteEntry[] = hasBillingAccess(user)
      ? data.clients.map((client) => ({
        id: `c-${client.id}`,
        group: 'Customers',
        label: <>{client.name}{client.phone ? <span className="ua-palette-sub"> · {client.phone}</span> : null}</>,
        keywords: `${client.name} ${client.phone}`.toLowerCase(),
        run: go(`/accounts/${client.id}`),
      }))
      : [];
    return [...actions, ...pages, ...orders, ...customers];
  }, [data, navigate]);

  const needle = query.trim().toLowerCase();
  const results = (needle
    ? entries.filter((entry) => entry.keywords.toLowerCase().includes(needle) || (typeof entry.label === 'string' && entry.label.toLowerCase().includes(needle)))
    : entries.filter((entry) => entry.group === 'Actions' || entry.group === 'Go to'))
    .slice(0, 12);
  const selected = Math.min(active, Math.max(results.length - 1, 0));

  const close = () => {
    setQuery('');
    setActive(0);
    onClose();
  };
  const choose = (entry: PaletteEntry | undefined) => {
    if (!entry) return;
    close();
    entry.run();
  };

  let lastGroup = '';
  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      closable={false}
      width={600}
      className="ua-palette"
      afterOpenChange={(visible) => { if (visible) inputRef.current?.focus(); }}
      destroyOnHidden
    >
      <Input
        ref={inputRef}
        variant="borderless"
        size="large"
        prefix={<SearchOutlined />}
        suffix={<kbd>esc</kbd>}
        placeholder="Search orders, customers or pages"
        value={query}
        onChange={(event) => { setQuery(event.target.value); setActive(0); }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') { event.preventDefault(); setActive((index) => Math.min(index + 1, results.length - 1)); }
          if (event.key === 'ArrowUp') { event.preventDefault(); setActive((index) => Math.max(index - 1, 0)); }
          if (event.key === 'Enter') { event.preventDefault(); choose(results[selected]); }
        }}
        aria-label="Search orders, customers or pages"
      />
      <div className="ua-palette-results" role="listbox">
        {results.map((entry, index) => {
          const header = entry.group !== lastGroup ? entry.group : null;
          lastGroup = entry.group;
          return (
            <div key={entry.id}>
              {header && <div className="ua-overline ua-palette-group">{header}</div>}
              <button
                type="button"
                role="option"
                aria-selected={index === selected}
                className={`ua-palette-item${index === selected ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(entry)}
              >
                <span className="ua-palette-label">{entry.label}</span>
                {entry.hint && <span className="ua-palette-hint">{entry.hint}</span>}
              </button>
            </div>
          );
        })}
        {!results.length && <div className="ua-palette-empty">Nothing matches “{query}”.</div>}
      </div>
    </Modal>
  );
}

export function ShortcutSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Keyboard shortcuts" width={440}>
      <dl className="ua-shortcuts">
        {SHORTCUTS.map((shortcut) => (
          <div key={shortcut.label} className="ua-shortcut-row">
            <dt>{shortcut.label}</dt>
            <dd>{shortcut.keys.map((key) => <kbd key={key}>{key}</kbd>)}</dd>
          </div>
        ))}
      </dl>
    </Modal>
  );
}
